import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { DensityMode, DensityContextType, DensityConfig } from '../types/density';
import { autoDetectDensityMode } from '../utils/densityHelpers';
import { safeStorage } from '../utils/safeStorage';

const DensityContext = createContext<DensityContextType | undefined>(undefined);

interface DensityProviderProps {
  children: ReactNode;
  defaultMode?: DensityMode;
  autoDetectScreenSize?: boolean;
  persistToLocalStorage?: boolean;
}

// Density configurations for each mode
const densityConfigs: Record<DensityMode, DensityConfig> = {
  compact: {
    mode: 'compact',
    paddingMultiplier: 0.5,
    marginMultiplier: 0.5,
    spacingMultiplier: 0.5,
    fontSizeMultiplier: 0.875,
    lineHeightMultiplier: 0.9,
  },
  normal: {
    mode: 'normal',
    paddingMultiplier: 1,
    marginMultiplier: 1,
    spacingMultiplier: 1,
    fontSizeMultiplier: 1,
    lineHeightMultiplier: 1,
  },
  spacious: {
    mode: 'spacious',
    paddingMultiplier: 1.25,
    marginMultiplier: 1.25,
    spacingMultiplier: 1.25,
    fontSizeMultiplier: 1.125,
    lineHeightMultiplier: 1.1,
  },
};

export const DensityProvider: React.FC<DensityProviderProps> = ({
  children,
  defaultMode,
  autoDetectScreenSize = true,
  persistToLocalStorage = true,
}) => {
  const [density, setDensityState] = useState<DensityMode>(() => {
    // Initialize density mode with this priority:
    // 1. localStorage (if persistence enabled)
    // 2. Auto-detection (if enabled)
    // 3. Default mode prop
    // 4. Fallback to 'normal'

    if (persistToLocalStorage && typeof window !== 'undefined') {
      const stored = safeStorage.getItem('simpledealer_density_mode') as DensityMode;
      if (stored && ['compact', 'normal', 'spacious'].includes(stored)) {
        return stored;
      }
    }

    if (autoDetectScreenSize) {
      return autoDetectDensityMode();
    }

    return defaultMode || 'normal';
  });

  // Handle screen size changes for auto-detection
  useEffect(() => {
    if (!autoDetectScreenSize) return;

    const handleResize = () => {
      // Only auto-adjust if user hasn't manually set a preference
      const hasManualPreference =
        persistToLocalStorage &&
        typeof window !== 'undefined' &&
        safeStorage.getItem('simpledealer_density_mode');

      if (!hasManualPreference) {
        const detectedMode = autoDetectDensityMode();
        if (detectedMode !== density) {
          setDensityState(detectedMode);
        }
      }
    };

    // Debounce resize events
    let timeoutId: NodeJS.Timeout;
    const debouncedResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(handleResize, 250);
    };

    window.addEventListener('resize', debouncedResize);
    return () => {
      window.removeEventListener('resize', debouncedResize);
      clearTimeout(timeoutId);
    };
  }, [density, autoDetectScreenSize, persistToLocalStorage]);

  const setDensity = (mode: DensityMode) => {
    setDensityState(mode);

    // Persist to localStorage if enabled
    if (persistToLocalStorage && typeof window !== 'undefined') {
      safeStorage.setItem('simpledealer_density_mode', mode);
    }

    // Add CSS custom properties to document root for advanced styling
    if (typeof document !== 'undefined') {
      const config = densityConfigs[mode];
      const root = document.documentElement;

      root.style.setProperty('--density-padding-multiplier', config.paddingMultiplier.toString());
      root.style.setProperty('--density-margin-multiplier', config.marginMultiplier.toString());
      root.style.setProperty('--density-spacing-multiplier', config.spacingMultiplier.toString());
      root.style.setProperty('--density-font-multiplier', config.fontSizeMultiplier.toString());
      root.style.setProperty(
        '--density-line-height-multiplier',
        config.lineHeightMultiplier.toString()
      );
      root.style.setProperty('--density-mode', mode);
    }
  };

  // Set initial CSS custom properties
  useEffect(() => {
    setDensity(density);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  const contextValue: DensityContextType = {
    density,
    setDensity,
    config: densityConfigs[density],
    isCompact: density === 'compact',
    isNormal: density === 'normal',
    isSpaciousMode: density === 'spacious',
  };

  return (
    <DensityContext.Provider value={contextValue}>
      <div className={`density-${density}`} data-density={density}>
        {children}
      </div>
    </DensityContext.Provider>
  );
};

export const useDensity = (): DensityContextType => {
  const context = useContext(DensityContext);
  if (context === undefined) {
    throw new Error('useDensity must be used within a DensityProvider');
  }
  return context;
};

// Higher-order component for class-based components
export function withDensity<P extends object>(
  Component: React.ComponentType<P & { density: DensityContextType }>
): React.FC<P> {
  return function WithDensityComponent(props: P) {
    const density = useDensity();
    return <Component {...props} density={density} />;
  };
}

// Custom hook for density-aware responsive design
export function useDensityBreakpoint() {
  const { density } = useDensity();
  const [screenWidth, setScreenWidth] = useState<number>(
    typeof window !== 'undefined' ? window.innerWidth : 1920
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleResize = () => setScreenWidth(window.innerWidth);

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Density-aware breakpoints
  const breakpoints = {
    compact: { sm: 640, md: 768, lg: 1024, xl: 1280 },
    normal: { sm: 640, md: 768, lg: 1024, xl: 1280 },
    spacious: { sm: 768, md: 896, lg: 1152, xl: 1440 }, // Larger breakpoints for spacious mode
  };

  const current = breakpoints[density];

  return {
    isSmUp: screenWidth >= current.sm,
    isMdUp: screenWidth >= current.md,
    isLgUp: screenWidth >= current.lg,
    isXlUp: screenWidth >= current.xl,
    screenWidth,
    breakpoints: current,
  };
}

// Utility hook for accessing density configurations
export function useDensityConfig() {
  const { density, config } = useDensity();

  return {
    ...config,
    density,
    cssVariables: {
      '--density-padding': `${config.paddingMultiplier}rem`,
      '--density-margin': `${config.marginMultiplier}rem`,
      '--density-spacing': `${config.spacingMultiplier}rem`,
      '--density-font-size': `${config.fontSizeMultiplier}rem`,
      '--density-line-height': config.lineHeightMultiplier,
    },
    inlineStyles: {
      paddingMultiplier: config.paddingMultiplier,
      marginMultiplier: config.marginMultiplier,
      spacingMultiplier: config.spacingMultiplier,
      fontSize: `${config.fontSizeMultiplier}em`,
      lineHeight: config.lineHeightMultiplier,
    },
  };
}
